import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CustomerHeader from '../../components/CustomerHeader';
import CustomerFooter from '../../components/CustomerFooter';
import { useCart } from '../../lib/cart';
import { formatInr } from '../../lib/pricing';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { EmptyState } from '../../components/States';
import { Trash2, Minus, Plus, ShoppingCart, ArrowRight } from 'lucide-react';

export default function Cart() {
    const nav = useNavigate();
    const { items, updateQty, removeItem, subtotal, totalWeight } = useCart();
    return (
        <div className="bg-white min-h-screen">
            <CustomerHeader />
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6">Your Cart</h1>
                {items.length === 0 ? (
                    <EmptyState icon={ShoppingCart} title="Your cart is empty" description="Start browsing to add products to your cart."
                        action={<Link to="/"><Button style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Continue Shopping</Button></Link>} />
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        <div className="lg:col-span-8 space-y-3" data-testid="cart-items">
                            {items.map(it => (
                                <Card key={it.product_id} className="p-4 flex items-center gap-4" data-testid={`cart-item-${it.sku}`}>
                                    <div className="h-16 w-16 rounded bg-neutral-100 overflow-hidden shrink-0">
                                        {it.image && <img src={it.image} alt={it.name} className="h-full w-full object-cover" onError={(e) => e.currentTarget.style.display = 'none'} />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium truncate">{it.name}</div>
                                        <div className="text-xs text-neutral-500 font-mono">{it.sku} · {it.weight_kg} kg</div>
                                        <div className="text-xs text-neutral-600 mt-0.5">{formatInr(it.unit_price)} / {it.unit_code || 'pcs'}</div>
                                    </div>
                                    <div className="inline-flex items-center border rounded-md">
                                        <button onClick={() => updateQty(it.product_id, it.qty - 1)} className="px-2 py-1"><Minus className="h-3.5 w-3.5" /></button>
                                        <span className="px-3 tabular-nums w-10 text-center" data-testid={`cart-qty-${it.sku}`}>{it.qty}</span>
                                        <button onClick={() => updateQty(it.product_id, it.qty + 1)} className="px-2 py-1"><Plus className="h-3.5 w-3.5" /></button>
                                    </div>
                                    <div className="w-24 text-right tabular-nums font-medium">{formatInr(it.unit_price * it.qty)}</div>
                                    <button onClick={() => removeItem(it.product_id)} className="p-2 text-red-600 hover:bg-red-50 rounded" data-testid={`cart-remove-${it.sku}`}><Trash2 className="h-4 w-4" /></button>
                                </Card>
                            ))}
                        </div>
                        <div className="lg:col-span-4">
                            <Card className="p-5 sticky top-24">
                                <div className="text-lg font-semibold mb-3">Order Summary</div>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between"><span className="text-neutral-600">Subtotal</span><span className="tabular-nums">{formatInr(subtotal)}</span></div>
                                    <div className="flex justify-between"><span className="text-neutral-600">Total weight</span><span className="tabular-nums">{totalWeight.toFixed(2)} kg</span></div>
                                    <div className="flex justify-between text-xs text-neutral-500"><span>Shipping</span><span>Calculated at checkout</span></div>
                                </div>
                                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                                    <span className="font-semibold">Estimated Total</span>
                                    <span className="text-xl font-bold tabular-nums">{formatInr(subtotal)}</span>
                                </div>
                                <Button className="w-full mt-4" onClick={() => nav('/checkout')} data-testid="cart-checkout-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                                    Proceed to Checkout <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                                <Link to="/" className="block text-center text-sm text-blue-700 hover:underline mt-3">Continue shopping</Link>
                            </Card>
                        </div>
                    </div>
                )}
            </div>
            <CustomerFooter />
        </div>
    );
}
